import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { ChevronLeft, Eye, EyeOff } from 'lucide-react';
import { changePassword } from "../services/authService";

// Define Yup validation schema
const schema = yup.object({
  current_password: yup.string().required('Current password is required'),
  new_password: yup.string().required('New password is required'),
  confirm_password: yup.string()
    .oneOf([yup.ref('new_password'), null], 'Passwords must match')
    .required('Confirm password is required'),
});

function AccountChangePassword() {
  const navigate = useNavigate();
  const [currentPasswordVisible, setCurrentPasswordVisible] = useState(false);
  const [newPasswordVisible, setNewPasswordVisible] = useState(false);
  const [confirmPasswordVisible, setConfirmPasswordVisible] = useState(false);

  const { register, handleSubmit, formState: { errors } } = useForm({
    resolver: yupResolver(schema),
  });

  const handleGoBack = () => {
    navigate("/account");
  };

  const toggleCurrentPasswordVisibility = () => {
    setCurrentPasswordVisible(prevState => !prevState);
  };

  const toggleNewPasswordVisibility = () => {
    setNewPasswordVisible(prevState => !prevState);
  };

  const toggleConfirmPasswordVisibility = () => {
    setConfirmPasswordVisible(prevState => !prevState);
  };

  const onSubmit = async (data) => {
    try {
      const response = await changePassword(data.current_password, data.new_password, data.confirm_password);
      if (response.success === true) {
        toast.success(response.message);
        navigate('/account');
      } else if (response.success === false) {
        toast.error(response.message);
      }
    } catch (error) {
      toast.error("An error occurred while changing the password.");
      console.error(error);
    }
  };

  const onError = (errors) => {
    Object.values(errors).forEach(error => {
      toast.error(error.message);
    });
  };

  return (
    <div className="flex items-center justify-center flex-col bg-black min-h-screen p-0">
      <div className="h-screen p-4 pb-20 flex-grow max-w-sm min-w-[375px] w-full bg-white ">
        <p className="text-base leading-6 mb-[30px] mt-0 tracking-wide font-lato font-normal flex items-center">
          <span className="font-black me-2">
            <ChevronLeft size={16} onClick={handleGoBack} />
          </span>
          Change Password
        </p>

        <form onSubmit={handleSubmit(onSubmit, onError)}>
          <div className="mb-[30px] relative">
            <label className="text-[14px] leading-[21px] text-gray-700 font-lato mb-2 block" htmlFor="current_password">
              Current Password
            </label>
            <input
              placeholder="************"
              type={currentPasswordVisible ? "text" : "password"}
              id="current_password"
              {...register('current_password')}
              className="w-full bg-gray-300 focus:border-blue-300 focus:outline-none rounded-md px-4 py-2"
            />
            {currentPasswordVisible ? (
              <Eye
                className="absolute right-2 top-9 text-gray-400 cursor-pointer"
                size={24}
                onClick={toggleCurrentPasswordVisibility}
              />
            ) : (
              <EyeOff
                className="absolute right-2 top-9 text-gray-400 cursor-pointer"
                size={24}
                onClick={toggleCurrentPasswordVisibility}
              />
            )}
          </div>

          <div className="mb-[30px] relative">
            <label className="text-[14px] leading-[21px] text-gray-700 font-lato mb-2 block" htmlFor="new_password">
              New Password
            </label>
            <input
              placeholder="************"
              type={newPasswordVisible ? "text" : "password"}
              id="new_password"
              {...register('new_password')}
              className="w-full bg-gray-300 focus:border-blue-300 focus:outline-none rounded-md px-4 py-2"
            />
            {newPasswordVisible ? (
              <Eye
                className="absolute right-2 top-9 text-gray-400 cursor-pointer"
                size={24}
                onClick={toggleNewPasswordVisibility}
              />
            ) : (
              <EyeOff
                className="absolute right-2 top-9 text-gray-400 cursor-pointer"
                size={24}
                onClick={toggleNewPasswordVisibility}
              />
            )}
          </div>

          <div className="mb-[30px] relative">
            <label className="text-[14px] leading-[21px] text-gray-700 font-lato mb-2 block" htmlFor="confirm_password">
              Confirm Password
            </label>
            <input
              placeholder="************"
              type={confirmPasswordVisible ? "text" : "password"}
              id="confirm_password"
              {...register('confirm_password')}
              className="w-full bg-gray-300 focus:border-blue-300 focus:outline-none rounded-md px-4 py-2"
            />
            {confirmPasswordVisible ? (
              <Eye
                className="absolute right-2 top-9 text-gray-400 cursor-pointer"
                size={24}
                onClick={toggleConfirmPasswordVisibility}
              />
            ) : (
              <EyeOff
                className="absolute right-2 top-9 text-gray-400 cursor-pointer"
                size={24}
                onClick={toggleConfirmPasswordVisibility}
              />
            )}
          </div>

          <div className="mb-[30px]">
            <button type="submit"
              className="bg-[#536895] w-full border border-[#536895] rounded-lg text-white text-base font-semibold py-3 px-12">
              Update Password
            </button>
          </div>
        </form>
      </div>
      <ToastContainer />
    </div>
  );
}

export default AccountChangePassword;